import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Clear as ClearIcon,
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  PushPin as PushPinIcon,
} from "@mui/icons-material";
import { textColorHandler } from "../Helpers.tsx";

export type NoteChecklistItem = {
  text: string;
  done?: boolean;
};

export type NoteLabelOption = {
  "@id"?: string;
  id?: string | number;
  name: string;
  color?: string;
};

export type NoteItem = {
  "@id"?: string;
  id?: string | number;
  note?: string | null;
  title?: string | null;
  pinned?: boolean;
  checkList?: NoteChecklistItem[] | null;
  labels?: NoteLabelOption[];
  user?: any;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type NoteValues = {
  note: string;
  title: string | null;
  pinned: boolean;
  checkList: NoteChecklistItem[];
  labels: string[];
};

type NotesPanelProps = {
  notes?: NoteItem[];
  labelOptions?: NoteLabelOption[];
  title?: React.ReactNode;
  emptyText?: string;
  addLabel?: string;
  noteLabel?: string;
  createLabel?: string;
  updateLabel?: string;
  cancelLabel?: string;
  deleteLabel?: string;
  editLabel?: string;
  showMoreLabel?: string;
  showMoreCount?: number;
  onShowMore?: () => void;
  onClose?: () => void;
  embedded?: boolean;
  onCreate: (values: NoteValues) => Promise<void>;
  onUpdate: (note: NoteItem, values: NoteValues) => Promise<void>;
  onDelete: (note: NoteItem) => Promise<void>;
  maxHeight?: string | number;
};

const getNoteId = (note: NoteItem) => note["@id"] ?? note.id;

const getAuthorName = (note: NoteItem) => {
  const user = note.user;

  if (!user) return "";
  if (typeof user === "string") return user;

  return (
    user.completeName ||
    user.name ||
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    user.email ||
    ""
  );
};

const formatNoteDate = (value?: string | null, locale?: string) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString(locale, {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const getChecklistProgress = (items: NoteChecklistItem[]) => {
  if (!items.length) return { value: 0, label: "0/0" };

  const completed = items.filter((item) => item.done).length;
  return {
    value: (completed / items.length) * 100,
    label: `${completed}/${items.length}`,
  };
};

const getNoteValues = (
  note: NoteItem,
  overrides: Partial<NoteValues> = {}
): NoteValues => ({
  note: note.note || "",
  title: note.title || null,
  pinned: Boolean(note.pinned),
  checkList: note.checkList || [],
  labels: (note.labels || [])
    .map((label) => label["@id"])
    .filter(Boolean) as string[],
  ...overrides,
});

const NotesPanel: React.FC<NotesPanelProps> = ({
  notes = [],
  labelOptions = [],
  title = "Notes",
  emptyText = "No notes yet.",
  addLabel = "Add note",
  noteLabel = "Note",
  createLabel = "Save",
  updateLabel = "Update",
  cancelLabel = "Cancel",
  deleteLabel = "Delete",
  editLabel = "Edit",
  showMoreLabel,
  showMoreCount = 0,
  onShowMore,
  onClose,
  embedded = false,
  onCreate,
  onUpdate,
  onDelete,
  maxHeight,
}) => {
  const { t, i18n } = useTranslation();
  const [editingNote, setEditingNote] = useState<NoteItem | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [pinnedDraft, setPinnedDraft] = useState(false);
  const [checklistDraft, setChecklistDraft] = useState<NoteChecklistItem[]>([]);
  const [labelDraft, setLabelDraft] = useState<NoteLabelOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [noteToDelete, setNoteToDelete] = useState<NoteItem | null>(null);

  const sortedNotes = useMemo(
    () =>
      [...notes].sort((left, right) => {
        if (Boolean(left.pinned) !== Boolean(right.pinned)) {
          return left.pinned ? -1 : 1;
        }

        const leftDate = new Date(
          left.updatedAt || left.createdAt || 0
        ).getTime();
        const rightDate = new Date(
          right.updatedAt || right.createdAt || 0
        ).getTime();

        return rightDate - leftDate;
      }),
    [notes]
  );

  const pinnedNotes = useMemo(() => sortedNotes.filter((n) => n.pinned), [sortedNotes]);
  const unpinnedNotes = useMemo(() => sortedNotes.filter((n) => !n.pinned), [sortedNotes]);

  const resetForm = () => {
    setEditingNote(null);
    setDraft("");
    setTitleDraft("");
    setPinnedDraft(false);
    setChecklistDraft([]);
    setLabelDraft([]);
    setError("");
    setShowForm(false);
  };

  const startCreate = () => {
    setEditingNote(null);
    setDraft("");
    setTitleDraft("");
    setPinnedDraft(false);
    setChecklistDraft([]);
    setLabelDraft([]);
    setError("");
    setShowForm(true);
  };

  const startEdit = (note: NoteItem) => {
    setEditingNote(note);
    setDraft(note.note || "");
    setTitleDraft(note.title || "");
    setPinnedDraft(Boolean(note.pinned));
    setChecklistDraft(
      (note.checkList || []).map((item) => ({
        text: item.text || "",
        done: Boolean(item.done),
      }))
    );
    setLabelDraft(note.labels || []);
    setError("");
    setShowForm(true);
  };

  const handleSubmit = async () => {
    const value = draft.trim();
    const checklist = checklistDraft
      .map((item) => ({ text: item.text.trim(), done: Boolean(item.done) }))
      .filter((item) => item.text);

    if (!value && !titleDraft.trim() && !checklist.length) return;

    const values: NoteValues = {
      note: value,
      title: titleDraft.trim() || null,
      pinned: pinnedDraft,
      checkList: checklist,
      labels: labelDraft
        .map((label) => label["@id"])
        .filter(Boolean) as string[],
    };

    setSaving(true);
    setError("");

    try {
      if (editingNote) {
        await onUpdate(editingNote, values);
      } else {
        await onCreate(values);
      }

      resetForm();
    } catch {
      setError(t("couldNotSaveNote"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (note: NoteItem) => {
    setSaving(true);
    setError("");

    try {
      await onDelete(note);

      if (getNoteId(editingNote || {}) === getNoteId(note)) {
        resetForm();
      }

      return true;
    } catch {
      setError(t("couldNotDeleteNote"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!noteToDelete) return;

    if (await handleDelete(noteToDelete)) {
      setNoteToDelete(null);
    }
  };

  const renderNote = (note: NoteItem, index: number) => {
    const author = getAuthorName(note);
    const date = formatNoteDate(
      note.updatedAt || note.createdAt,
      i18n.resolvedLanguage || i18n.language
    );
    const checklist = note.checkList || [];
    const checklistProgress = getChecklistProgress(checklist);

    return (
      <Box
        key={getNoteId(note) || `note-${index}`}
        sx={{
          p: 1.25,
          borderRadius: 1.5,
          border: "1px solid rgba(148, 163, 184, 0.55)",
          backgroundColor: "#ffffff",
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={1}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {note.title ? (
              <Typography fontSize="0.86rem" fontWeight={900} mb={0.35}>
                {note.title}
              </Typography>
            ) : null}

            <Typography
              fontSize="0.9rem"
              lineHeight={1.4}
              whiteSpace="pre-wrap"
            >
              {note.note || "—"}
            </Typography>

            {note.labels?.length ? (
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                mt={0.75}
              >
                {note.labels.map((label, labelIndex) => (
                  <Chip
                    key={label["@id"] || label.id || labelIndex}
                    size="small"
                    label={label.name}
                    sx={{
                      height: 21,
                      fontSize: "0.68rem",
                      backgroundColor:
                        label.color ||
                        "rgba(59, 130, 246, 0.12)",
                      color: label.color
                        ? textColorHandler(label.color)
                        : undefined,
                    }}
                  />
                ))}
              </Stack>
            ) : null}

            {checklist.length ? (
              <Box mt={1}>
                <Stack spacing={0.35}>
                  {checklist.map((item, itemIndex) => (
                    <Stack
                      key={itemIndex}
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                    >
                      <Checkbox
                        size="small"
                        checked={Boolean(item.done)}
                        sx={{ p: 0 }}
                        onChange={() =>
                          onUpdate(
                            note,
                            getNoteValues(note, {
                              checkList: checklist.map(
                                (currentItem, currentIndex) =>
                                  currentIndex === itemIndex
                                    ? {
                                        ...currentItem,
                                        done: !currentItem.done,
                                      }
                                    : currentItem
                              ),
                            })
                          )
                        }
                      />
                      <Typography
                        fontSize="0.75rem"
                        sx={{
                          textDecoration: item.done
                            ? "line-through"
                            : "none",
                          color: item.done
                            ? "text.disabled"
                            : "text.primary",
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  mt={0.75}
                >
                  <LinearProgress
                    variant="determinate"
                    value={checklistProgress.value}
                    color={
                      checklistProgress.value === 100
                        ? "success"
                        : "primary"
                    }
                    sx={{ flex: 1, borderRadius: 1 }}
                  />
                  <Typography fontSize="0.68rem" color="text.secondary">
                    {checklistProgress.label}
                  </Typography>
                </Stack>
              </Box>
            ) : null}

            {author || date ? (
              <Typography
                display="block"
                mt={0.75}
                fontSize="0.7rem"
                color="text.secondary"
              >
                {author}
                {author && date ? " • " : ""}
                {date}
              </Typography>
            ) : null}
          </Box>

          <Stack direction="row" spacing={0.25}>
            <Tooltip title={note.pinned ? t("unpin") : t("pin")}>
              <IconButton
                size="small"
                disabled={saving}
                onClick={() =>
                  onUpdate(
                    note,
                    getNoteValues(note, {
                      pinned: !note.pinned,
                    })
                  )
                }
              >
                <PushPinIcon
                  fontSize="small"
                  color={note.pinned ? "primary" : "inherit"}
                />
              </IconButton>
            </Tooltip>

            <Tooltip title={editLabel}>
              <IconButton
                size="small"
                onClick={() => startEdit(note)}
                disabled={saving}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={deleteLabel}>
              <IconButton
                size="small"
                color="error"
                onClick={() => setNoteToDelete(note)}
                disabled={saving}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Paper
      variant="outlined"
      sx={{
        borderRadius: embedded ? 0 : 2,
        borderColor: embedded ? "transparent" : "rgba(15, 23, 42, 0.1)",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 1.5, py: 1.25 }}
      >
        <Typography fontSize="0.95rem" fontWeight={900}>
          {title}
        </Typography>

        <Stack direction="row" spacing={0.25} alignItems="center">
          <Tooltip title={addLabel}>
            <IconButton size="small" onClick={startCreate} disabled={saving}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {onClose ? (
            <Tooltip title={t("close")}>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>

      <Divider />

      {showForm ? (
        <>
          <Box sx={{ p: 1.5 }}>
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize="0.86rem" fontWeight={900}>
                  {editingNote ? editLabel : addLabel}
                </Typography>

                <IconButton
                  size="small"
                  onClick={resetForm}
                  disabled={saving}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Stack>

              <TextField
                autoFocus
                fullWidth
                size="small"
                label={t("title")}
                value={titleDraft}
                onChange={(event) => setTitleDraft(event.target.value)}
                disabled={saving}
              />

              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                size="small"
                label={noteLabel}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                disabled={saving}
                error={Boolean(error)}
                helperText={error}
              />

              <Autocomplete
                multiple
                size="small"
                options={labelOptions}
                value={labelDraft}
                onChange={(_, nextLabels) => setLabelDraft(nextLabels)}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, selected) =>
                  (option["@id"] || option.id) ===
                  (selected["@id"] || selected.id)
                }
                renderTags={(selectedLabels, getTagProps) =>
                  selectedLabels.map((label, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={label["@id"] || label.id || index}
                      size="small"
                      label={label.name}
                      sx={{
                        backgroundColor:
                          label.color || "rgba(59, 130, 246, 0.12)",
                        color: label.color
                          ? textColorHandler(label.color)
                          : undefined,
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label={t("labels")} />
                )}
              />

              <Box>
                <Typography fontSize="0.8rem" fontWeight={900} mb={0.75}>
                  {t("checklist")}
                </Typography>

                <Stack spacing={1}>
                  {checklistDraft.map((item, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                    >
                      <Checkbox
                        size="small"
                        checked={Boolean(item.done)}
                        onChange={() =>
                          setChecklistDraft((current) =>
                            current.map((checklistItem, itemIndex) =>
                              itemIndex === index
                                ? {
                                    ...checklistItem,
                                    done: !checklistItem.done,
                                  }
                                : checklistItem
                            )
                          )
                        }
                      />
                      <TextField
                        fullWidth
                        size="small"
                        value={item.text}
                        onChange={(event) =>
                          setChecklistDraft((current) =>
                            current.map((checklistItem, itemIndex) =>
                              itemIndex === index
                                ? {
                                    ...checklistItem,
                                    text: event.target.value,
                                  }
                                : checklistItem
                            )
                          )
                        }
                        label={t("checklistItem")}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setChecklistDraft((current) =>
                            current.filter(
                              (_, itemIndex) => itemIndex !== index
                            )
                          )
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setChecklistDraft((current) => [
                        ...current,
                        { text: "", done: false },
                      ])
                    }
                    sx={{ alignSelf: "flex-start", textTransform: "none" }}
                  >
                    {t("addChecklistItem")}
                  </Button>
                </Stack>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={pinnedDraft}
                    onChange={(event) =>
                      setPinnedDraft(event.target.checked)
                    }
                  />
                }
                label={t("pinned")}
              />

              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button onClick={resetForm} disabled={saving}>
                  {cancelLabel}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={
                    saving ||
                    (!draft.trim() &&
                      !titleDraft.trim() &&
                      !checklistDraft.some((item) => item.text.trim()))
                  }
                  startIcon={saving ? <CircularProgress size={15} /> : undefined}
                >
                  {editingNote ? updateLabel : createLabel}
                </Button>
              </Stack>
            </Stack>
          </Box>
          <Divider />
        </>
      ) : null}

      <Box sx={{ p: 1.5, flex: 1, minHeight: 0, overflowY: "auto" }}>
        {error && !showForm ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : null}

        {sortedNotes.length ? (
          <Stack spacing={1.5}>
            {pinnedNotes.length > 0 ? (
              <>
                <Typography fontSize="0.75rem" fontWeight={900} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {t("pinned")}
                </Typography>
                <Stack spacing={1}>
                  {pinnedNotes.map((note, index) => renderNote(note, index))}
                </Stack>
              </>
            ) : null}

            {unpinnedNotes.length > 0 ? (
              <>
                <Typography fontSize="0.75rem" fontWeight={900} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {t("others")}
                </Typography>
                <Stack spacing={1}>
                  {unpinnedNotes.map((note, index) => renderNote(note, index))}
                </Stack>
              </>
            ) : null}

            {showMoreCount > 0 && onShowMore ? (
              <Button
                size="small"
                variant="text"
                onClick={onShowMore}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                {showMoreLabel || showMoreCount}
              </Button>
            ) : null}
          </Stack>
        ) : (
          <Alert severity="info">{emptyText}</Alert>
        )}
      </Box>
      </Paper>

      <Dialog
        open={Boolean(noteToDelete)}
        onClose={() => !saving && setNoteToDelete(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("confirmDelete")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("deleteNoteConfirmation")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteToDelete(null)} disabled={saving}>
            {cancelLabel}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={15} /> : undefined}
          >
            {deleteLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotesPanel;
